import {
  INCIDENT_KIND_LABELS,
  INCIDENT_STATUS_LABELS,
  INCIDENT_STATUSES,
  subject,
  type AssetDetail,
  type Incident,
  type IncidentStatus,
  type Paginated,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip, StatusIndicator } from '@ftthnms/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell, BellOff, CheckCheck, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AssetDrawer } from '../components/AssetDrawer';
import { AssetForm } from '../components/AssetForm';
import { Field, FormDialog, Pager } from '../components/common';
import { useToast } from '../components/Toast';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, dateTime, nf, qs, send } from '../lib/api';
import { toIndicator } from '../lib/monitoring';
import { useIncidentSummary } from '../lib/incidents';
import { useRealtimeConnected } from '../lib/realtime';
import { useSession } from '../lib/session';

type Panel = { kind: 'view'; id: string } | { kind: 'edit'; asset: AssetDetail };
type Action = { kind: 'ack' | 'resolve'; incident: Incident };

/** Insiden (MON-10, in-app): device outages opened and closed by monitoring, acknowledged by Admin/NOC. */
export function IncidentsPage() {
  const { area, ability } = useSession();
  const live = useRealtimeConnected();
  const navigate = useNavigate();
  const [status, setStatus] = useState<IncidentStatus>('open');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const now = useNow(30_000);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [debouncedQ, status, area]);

  const summary = useIncidentSummary();
  const list = useQuery({
    queryKey: ['incidents', 'list', { area, status, q: debouncedQ, page }],
    queryFn: () =>
      api<Paginated<Incident>>(`/api/v1/incidents${qs({ area, status, q: debouncedQ, page, pageSize })}`),
    placeholderData: keepPreviousData,
  });
  const data = list.data;
  const counts: Record<IncidentStatus, number | undefined> = {
    open: summary.data?.open,
    acknowledged: summary.data?.acknowledged,
    resolved: undefined,
  };

  return (
    <div className="fn-page-body">
      <section className="fn-card" style={{ overflow: 'hidden' }}>
        <div className="fn-toolbar">
          <div className="fn-segmented" role="tablist" aria-label="Status insiden">
            {INCIDENT_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                className="fn-segmented__item"
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
              >
                {INCIDENT_STATUS_LABELS[s]}
                {counts[s] ? (
                  <Badge tone={s === 'open' ? 'danger' : 'warning'} square>
                    {nf.format(counts[s]!)}
                  </Badge>
                ) : null}
              </button>
            ))}
          </div>
          <div className="fn-input-group" style={{ flex: 1, maxWidth: 260, minWidth: 160 }}>
            <Search className="i" aria-hidden />
            <input
              className="fn-input"
              aria-label="Cari insiden"
              placeholder="Cari kode atau nama perangkat…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <span className="fn-toolbar__spacer" />
          {summary.data && (
            <span className="fn-hint">{nf.format(summary.data.resolvedToday)} selesai hari ini</span>
          )}
          <Badge tone={live ? 'success' : 'neutral'} live={live} dot>
            {live ? 'Live' : 'Offline'}
          </Badge>
        </div>

        {data && data.total === 0 ? (
          <div style={{ padding: 20 }}>
            <EmptyState
              icon={
                status === 'resolved' ? (
                  <Bell className="i i--xl" aria-hidden />
                ) : (
                  <BellOff className="i i--xl" aria-hidden />
                )
              }
              title={
                status === 'open'
                  ? 'Tidak ada insiden aktif'
                  : status === 'acknowledged'
                    ? 'Tidak ada insiden yang sedang ditangani'
                    : 'Belum ada insiden selesai'
              }
              text="Insiden dibuat otomatis saat perangkat yang dipantau offline, dan ditutup otomatis saat perangkat kembali online."
            />
          </div>
        ) : (
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                <tr>
                  <th>Perangkat</th>
                  <th>Kejadian</th>
                  <th>Mulai</th>
                  <th>Durasi</th>
                  <th>Status sekarang</th>
                  <th>Penanganan</th>
                  <th className="is-end">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((i) => {
                  const canAck = ability.can('ack', subject('Alert', { areaCode: i.areaCode }));
                  return (
                    <tr
                      key={i.id}
                      onClick={() => setPanel({ kind: 'view', id: i.assetId })}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="fn-asset">
                          <AssetGlyph type={i.typeCode} />
                          <div>
                            <div className="fn-asset__name">{i.assetName}</div>
                            <div className="fn-asset__code">
                              {i.assetCode}
                              {i.areaCode && (
                                <span style={{ marginLeft: 6 }}>
                                  <RegionChip code={i.areaCode} />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge tone={i.severity === 'critical' ? 'danger' : 'warning'}>
                          {INCIDENT_KIND_LABELS[i.kind] ?? i.kind}
                        </Badge>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{dateTime.format(new Date(i.startedAt))}</td>
                      <td className="num" style={{ whiteSpace: 'nowrap' }}>
                        {duration(Date.parse(i.startedAt), i.resolvedAt ? Date.parse(i.resolvedAt) : now)}
                      </td>
                      <td>
                        {i.deviceStatus ? <StatusIndicator status={toIndicator(i.deviceStatus)} /> : '—'}
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        {i.status === 'resolved' ? (
                          <span className="fn-hint">
                            {i.resolvedBy ? `Ditutup ${i.resolvedBy}` : 'Pulih otomatis'}
                          </span>
                        ) : i.acknowledgedBy ? (
                          <span className="fn-hint">Diakui {i.acknowledgedBy}</span>
                        ) : (
                          <span className="fn-hint">Belum ditangani</span>
                        )}
                        {i.ackNote && <div style={{ fontSize: 12 }}>{i.ackNote}</div>}
                      </td>
                      <td className="is-end" onClick={(e) => e.stopPropagation()}>
                        {canAck && i.status !== 'resolved' && (
                          <div className="flex justify-end gap-2">
                            {i.status === 'open' && (
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => setAction({ kind: 'ack', incident: i })}
                              >
                                Akui
                              </Button>
                            )}
                            <Button
                              size="sm"
                              icon={<CheckCheck className="i" aria-hidden />}
                              onClick={() => setAction({ kind: 'resolve', incident: i })}
                            >
                              Tutup
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pager
          total={data?.total ?? 0}
          unit="insiden"
          page={page}
          pageSize={pageSize}
          onPage={setPage}
          note={<span className="fn-hint"> · notifikasi Telegram menyusul</span>}
        />
      </section>

      {action && <ActionDialog action={action} onClose={() => setAction(null)} />}
      {panel?.kind === 'view' && (
        <AssetDrawer
          key={panel.id}
          id={panel.id}
          initialTab="monitoring"
          onClose={() => setPanel(null)}
          onEdit={(asset) => setPanel({ kind: 'edit', asset })}
          onOpenAsset={(id) => setPanel({ kind: 'view', id })}
          onFocus={(asset) => void navigate({ to: '/map', search: { focus: asset.id } })}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'edit' && (
        <AssetForm
          key={`edit-${panel.asset.id}`}
          mode="edit"
          asset={panel.asset}
          onClose={() => setPanel({ kind: 'view', id: panel.asset.id })}
          onSaved={(a) => setPanel({ kind: 'view', id: a.id })}
        />
      )}
    </div>
  );
}

function ActionDialog({ action, onClose }: { action: Action; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const { kind, incident } = action;
  return (
    <FormDialog
      title={kind === 'ack' ? `Akui insiden ${incident.assetCode}` : `Tutup insiden ${incident.assetCode}`}
      submitLabel={kind === 'ack' ? 'Akui' : 'Tutup insiden'}
      onClose={onClose}
      onSubmit={async () => {
        await send('POST', `/api/v1/incidents/${incident.id}/${kind === 'ack' ? 'ack' : 'resolve'}`, {
          note,
        });
        await qc.invalidateQueries({ queryKey: ['incidents'] });
        toast({
          tone: 'success',
          text:
            kind === 'ack'
              ? `Insiden ${incident.assetCode} diakui.`
              : `Insiden ${incident.assetCode} ditutup.`,
        });
      }}
    >
      {() => (
        <>
          <p className="fn-card__desc">
            {kind === 'ack'
              ? 'Menandai insiden sedang ditangani. Insiden tetap tercatat dan tertutup otomatis saat perangkat online lagi.'
              : 'Menutup insiden secara manual, mis. perangkat sengaja dimatikan. Bila perangkat masih offline, insiden baru tidak dibuat sampai perangkat online lalu offline lagi.'}
          </p>
          <Field label="Catatan" hint="Opsional, mis. teknisi sedang menuju lokasi">
            <textarea
              className="fn-input"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </>
      )}
    </FormDialog>
  );
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

function duration(from: number, to: number): string {
  const min = Math.max(0, Math.round((to - from) / 60_000));
  if (min < 60) return `${min} mnt`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} j ${min % 60} mnt`;
  return `${Math.floor(h / 24)} hr ${h % 24} j`;
}
